import {
  FormDialog,
  Notice,
  ProblemNotice,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@quovy/ui";
import { Button } from "@quovy/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@quovy/ui/dialog";
import { Input } from "@quovy/ui/input";
import { useEffect, useState } from "react";

import type { IngressProfile } from "@quovy/control-plane-contract";
import {
  isCustomerLoginOrigin,
  isProductionIngressProfile,
  parseIngressConfiguration,
} from "@quovy/control-plane-contract";

import type {
  AgentTokenResult,
  CreatePortalEnvironmentInput,
  CreatePortalEnvironmentResult,
} from "../lib/api";
import type { EndpointPairValue } from "../lib/environment-endpoints";
import { endpointPairValid } from "../lib/environment-endpoints";
import { normalizeSlugInput, slugify } from "../lib/slug";

import { AgentTokenDialog } from "./agent-token-dialog";
import { EndpointPairFields } from "./endpoint-pair-fields";
import { defaultIngressProfile, IngressProfileFields } from "./ingress-profile-fields";
import { ManagedAwsTrust } from "./managed-aws-trust";

type Cloud = CreatePortalEnvironmentInput["cloud"];
type ProvisioningKind = CreatePortalEnvironmentInput["provisioning"]["kind"];

const DEFAULT_REGION: Record<Cloud, string> = {
  aws: "us-east-2",
  azure: "eastus2",
};

export function RegisterEnvironmentDialog({
  open,
  onOpenChange,
  onCreate,
  onMint,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: CreatePortalEnvironmentInput) => Promise<CreatePortalEnvironmentResult>;
  onMint: (environment: { id: string; slug: string }) => Promise<AgentTokenResult>;
}) {
  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [cloud, setCloud] = useState<Cloud>("aws");
  const [provisioningKind, setProvisioningKind] = useState<ProvisioningKind>("customer_provided");
  const [awsAccountId, setAwsAccountId] = useState("");
  const [region, setRegion] = useState(DEFAULT_REGION.aws);
  const [endpoints, setEndpoints] = useState<EndpointPairValue>({
    webOrigin: "",
    apiOrigin: "",
  });
  const [ingressProfile, setIngressProfile] = useState<IngressProfile>(
    defaultIngressProfile("aws"),
  );
  const [certificate, setCertificate] = useState("");
  const [created, setCreated] = useState<CreatePortalEnvironmentResult | null>(null);
  const [mintOpen, setMintOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDisplayName("");
    setSlug("");
    setSlugTouched(false);
    setCloud("aws");
    setProvisioningKind("customer_provided");
    setAwsAccountId("");
    setRegion(DEFAULT_REGION.aws);
    setEndpoints({ webOrigin: "", apiOrigin: "" });
    setIngressProfile(defaultIngressProfile("aws"));
    setCertificate("");
    setCreated(null);
    setMintOpen(false);
    setError(null);
  }, [open]);

  const close = () => {
    setCreated(null);
    setMintOpen(false);
    onOpenChange(false);
  };

  const endpointsValid = endpointPairValid(endpoints);
  const endpointsFilled = Boolean(endpoints.webOrigin && endpoints.apiOrigin);
  const parsedIngress = parseIngressConfiguration({
    version: 1,
    profile: ingressProfile,
    certificate: certificate.trim(),
  });
  const customerOrigins =
    !endpointsFilled ||
    (isCustomerLoginOrigin(endpoints.webOrigin) && isCustomerLoginOrigin(endpoints.apiOrigin));
  const rehearsalBlocked = Boolean(
    endpointsFilled && isProductionIngressProfile(ingressProfile) && !customerOrigins,
  );

  const submit = async () => {
    setError(null);
    try {
      const normalizedSlug = slugify(slug);
      setSlug(normalizedSlug);
      setCreated(
        await onCreate({
          slug: normalizedSlug,
          display_name: displayName.trim(),
          cloud,
          region: region.trim(),
          provisioning:
            provisioningKind === "customer_provided"
              ? { kind: "customer_provided" }
              : { kind: "quovy_managed_aws", aws_account_id: awsAccountId },
          ...(endpoints.webOrigin && endpoints.apiOrigin && parsedIngress
            ? {
                endpoints: {
                  web_origin: endpoints.webOrigin,
                  api_origin: endpoints.apiOrigin,
                },
                ingress: parsedIngress,
              }
            : {}),
        }),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const valid =
    displayName.trim().length > 0 &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) &&
    region.trim().length > 0 &&
    (provisioningKind !== "quovy_managed_aws" || /^\d{12}$/.test(awsAccountId)) &&
    endpointsValid &&
    (!endpointsFilled || parsedIngress !== null) &&
    !rehearsalBlocked;
  const dirty =
    displayName.length > 0 ||
    slug.length > 0 ||
    cloud !== "aws" ||
    provisioningKind !== "customer_provided" ||
    awsAccountId.length > 0 ||
    region !== DEFAULT_REGION.aws ||
    endpoints.webOrigin.length > 0 ||
    endpoints.apiOrigin.length > 0 ||
    certificate.length > 0;

  const trust =
    created?.provisioning?.kind === "quovy_managed_aws" ? created.provisioning.trust : null;

  return (
    <>
      <FormDialog
        open={open && !created}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Register environment"
        onSubmit={submit}
        closeOnSubmit={false}
        confirmText="Register environment"
        loadingText="Registering…"
        disabled={!valid}
        isDirty={dirty}
        className="max-w-lg"
      >
        <div className="grid gap-4">
          {/* deliberate-ui-prose: registration does not make the environment deployable */}
          <Notice tone="neutral">
            Deployments stay blocked until infrastructure handover is complete.
          </Notice>

          <div className="grid gap-2">
            <label htmlFor="environment-display-name" className="text-sm font-medium">
              Display name
            </label>
            <Input
              id="environment-display-name"
              value={displayName}
              required
              autoFocus
              autoComplete="off"
              onChange={(event) => {
                const value = event.target.value;
                setDisplayName(value);
                if (!slugTouched) setSlug(slugify(value));
              }}
            />
          </div>

          <div className="grid gap-2">
            <label htmlFor="environment-slug" className="text-sm font-medium">
              Environment slug
            </label>
            <Input
              id="environment-slug"
              value={slug}
              required
              autoComplete="off"
              onChange={(event) => {
                setSlugTouched(true);
                setSlug(normalizeSlugInput(event.target.value));
              }}
              onBlur={() => setSlug((value) => slugify(value))}
            />
            {displayName.trim() && !slug ? (
              <ProblemNotice
                title="Environment slug is empty"
                howToResolve="Enter at least one lowercase letter or number in the Environment slug field."
                resolution={{ kind: "in-place" }}
              >
                The display name contains no characters that can be used in an environment slug.
              </ProblemNotice>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <label htmlFor="environment-cloud" className="text-sm font-medium">
                Cloud
              </label>
              <Select
                value={cloud}
                onValueChange={(value: Cloud) => {
                  const previousDefault = DEFAULT_REGION[cloud];
                  setCloud(value);
                  setIngressProfile(defaultIngressProfile(value));
                  setCertificate("");
                  if (value !== "aws") setProvisioningKind("customer_provided");
                  if (!region || region === previousDefault) setRegion(DEFAULT_REGION[value]);
                }}
              >
                <SelectTrigger id="environment-cloud" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aws">AWS</SelectItem>
                  <SelectItem value="azure">Azure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label htmlFor="environment-region" className="text-sm font-medium">
                Region
              </label>
              <Input
                id="environment-region"
                value={region}
                required
                autoComplete="off"
                onChange={(event) => setRegion(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <label htmlFor="environment-provisioning" className="text-sm font-medium">
              Infrastructure
            </label>
            <Select
              value={provisioningKind}
              onValueChange={(value: ProvisioningKind) => setProvisioningKind(value)}
            >
              <SelectTrigger id="environment-provisioning" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer_provided">Use existing infrastructure</SelectItem>
                {cloud === "aws" ? (
                  <SelectItem value="quovy_managed_aws">Quovy provisions AWS</SelectItem>
                ) : null}
              </SelectContent>
            </Select>
          </div>

          {provisioningKind === "quovy_managed_aws" ? (
            <div className="grid gap-2">
              <label htmlFor="environment-aws-account-id" className="text-sm font-medium">
                AWS account ID
              </label>
              <Input
                id="environment-aws-account-id"
                value={awsAccountId}
                required
                inputMode="numeric"
                autoComplete="off"
                maxLength={12}
                onChange={(event) => setAwsAccountId(event.target.value.replace(/\D/g, ""))}
              />
            </div>
          ) : null}

          <fieldset className="grid gap-3 rounded-lg border p-3">
            <legend className="px-1 text-sm font-medium">Public endpoints (optional)</legend>
            <EndpointPairFields idPrefix="environment" value={endpoints} onChange={setEndpoints} />
            {endpointsFilled ? (
              <IngressProfileFields
                idPrefix="environment"
                cloud={cloud}
                profile={ingressProfile}
                certificate={certificate}
                onProfileChange={setIngressProfile}
                onCertificateChange={setCertificate}
              />
            ) : null}
            {rehearsalBlocked ? (
              <ProblemNotice
                title="Rehearsal hostname cannot be the customer login URL"
                howToResolve="Replace nip.io, sslip.io, .invalid, or localhost with customer DNS on port 443, or switch to the cert-manager rehearsal profile."
                resolution={{ kind: "in-place" }}
              >
                Production ingress profiles reject disposable hostnames. A port-forward or rehearsal
                hostname is not a customer login path.
              </ProblemNotice>
            ) : null}
          </fieldset>

          {error ? (
            <ProblemNotice
              title="Environment was not registered"
              howToResolve="Correct any rejected field in this form, then retry."
              resolution={{ kind: "in-place" }}
              live
            >
              The portal refused the new {cloud.toUpperCase()} environment: {error}
            </ProblemNotice>
          ) : null}
        </div>
      </FormDialog>

      <Dialog
        open={open && Boolean(created) && !mintOpen}
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        <DialogContent aria-describedby={undefined} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{created?.environment} registered</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            {trust ? (
              <ManagedAwsTrust environmentSlug={created!.environment} trust={trust} />
            ) : null}
            {created?.next.length ? (
              <ol className="portal-next">
                {created.next.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={close}>
              Close
            </Button>
            <Button onClick={() => setMintOpen(true)}>Issue token</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {created ? (
        <AgentTokenDialog
          open={open && mintOpen}
          environment={{ id: created.public_id, slug: created.environment }}
          mode="initial"
          onOpenChange={(next) => {
            if (!next) close();
          }}
          onMint={onMint}
        />
      ) : null}
    </>
  );
}
